import { motion } from "framer-motion";
import { Container, Truck, Store, Radio, Wrench, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    icon: Container,
    title: "Importación Estratégica de Electrónica",
    desc: "Gestionamos el ciclo completo de importación desde fábricas globales, dominando trámites aduanales y normativas para introducir tecnología de consumo y línea blanca con costos optimizados.",
    span: "lg:col-span-2",
    dark: true,
  },
  {
    icon: Truck,
    title: "Logística y Distribución de Alto Alcance",
    desc: "Operamos una red de suministro robusta que asegura el traslado seguro de equipos sensibles, desde smartphones hasta electrodomésticos, garantizando puntualidad en entregas a nivel nacional.",
    span: "lg:col-span-1",
  },
  {
    icon: Store,
    title: "Comercialización Omnicanal (Mayoreo y Menudeo)",
    desc: "Ejecutamos procesos de venta masiva para distribuidores y atención especializada para el usuario final, manteniendo un inventario dinámico de televisores, computadoras y componentes originales.",
    span: "lg:col-span-1",
  },
  {
    icon: Radio,
    title: "Infraestructura en Telecomunicaciones y Fibra Óptica",
    desc: "Proveemos e instalamos equipos críticos como routers, antenas y cableado estructurado, respaldando la conectividad de nuestros clientes con tecnología de punta y personal certificado.",
    span: "lg:col-span-2",
    dark: true,
  },
  {
    icon: Wrench,
    title: "Soporte Técnico y Gestión de Repuestos",
    desc: "Ofrecemos un centro de servicio para el mantenimiento preventivo y correctivo, asegurando la disponibilidad inmediata de baterías, pantallas y piezas de reemplazo para todo nuestro catálogo.",
    span: "lg:col-span-3",
  },
];

export const Servicios = () => {
  return (
    <section
      id="servicios"
      data-testid="servicios-section"
      className="relative py-20 md:py-28 bg-white"
    >
      <div className="absolute inset-0 tech-grid-bg" />
      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-7">
            <div className="font-mono-tech text-[11px] tracking-[0.32em] uppercase text-[#843C0C] mb-4 flex items-center gap-2">
              <span className="h-px w-8 bg-[#843C0C]" />
              Cartera
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#203864] tracking-tight leading-[1.05]">
              Cartera de servicios Nexora.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4">
            <p className="text-[#3B3838]/80 text-base md:text-lg leading-relaxed">
              Cinco unidades de negocio integradas para cubrir el ciclo completo: desde
              fábrica global hasta soporte postventa.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#3B3838]/15">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const isDark = s.dark;
            return (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                className={`${s.span} ${
                  isDark ? "bg-[#203864] text-white" : "bg-white text-[#3B3838]"
                } p-7 md:p-9 group relative overflow-hidden`}
                data-testid={`service-card-${i}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div
                    className={`h-12 w-12 flex items-center justify-center ${
                      isDark
                        ? "bg-[#843C0C] text-white"
                        : "bg-[#203864] text-white group-hover:bg-[#843C0C]"
                    } transition-colors`}
                  >
                    <Icon size={22} />
                  </div>
                  <span
                    className={`font-mono-tech text-[11px] tracking-[0.24em] ${
                      isDark ? "text-[#FCD7C5]" : "text-[#843C0C]"
                    }`}
                  >
                    SVC.0{i + 1}
                  </span>
                </div>

                <h3
                  className={`text-xl md:text-2xl font-semibold tracking-tight leading-snug mb-4 ${
                    isDark ? "text-white" : "text-[#203864]"
                  }`}
                >
                  {s.title}
                </h3>
                <p
                  className={`text-sm md:text-[15px] leading-relaxed ${
                    isDark ? "text-white/75" : "text-[#3B3838]/80"
                  }`}
                >
                  {s.desc}
                </p>

                <div className="mt-6 pt-5 border-t border-current/15 flex items-center justify-between">
                  <span
                    className={`font-mono-tech text-[10px] tracking-[0.28em] uppercase ${
                      isDark ? "text-white/60" : "text-[#3B3838]/60"
                    }`}
                  >
                    Categoría · Operación
                  </span>
                  <ArrowUpRight
                    size={20}
                    className={`${
                      isDark ? "text-[#FCD7C5]" : "text-[#843C0C]"
                    } group-hover:rotate-45 transition-transform`}
                  />
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* CTA banner — verbatim text */}
        <div
          data-testid="cta-banner"
          className="mt-16 relative bg-[#843C0C] text-white p-8 md:p-14 overflow-hidden"
        >
          <div className="absolute inset-0 tech-grid-bg-dark opacity-40" />
          <svg
            className="absolute -right-20 -top-10 h-[120%] w-[60%] opacity-20"
            viewBox="0 0 400 400"
          >
            <path className="circuit-line" d="M0 200 L120 200 L160 240 L260 240 L300 200 L400 200" />
            <path className="circuit-line alt" d="M50 50 L50 150 L150 250 L150 350" />
          </svg>
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-9">
              <div className="font-mono-tech text-[11px] tracking-[0.32em] uppercase text-white/80 mb-4">
                Aliados estratégicos
              </div>
              <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]">
                "CONTÁCTANOS PARA LA IMPORTACIÓN DE TODOS TUS PRODUCTOS, EN NEXORA SOMOS
                TUS MEJORES ALIADOS"
              </p>
            </div>
            <div className="lg:col-span-3 flex lg:justify-end">
              <a
                href="#contacto"
                data-testid="cta-banner-button"
                className="inline-flex items-center gap-2 bg-white text-[#843C0C] hover:bg-[#203864] hover:text-white px-7 py-4 font-semibold tracking-wide transition-colors"
              >
                Iniciar contacto
                <ArrowUpRight size={20} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
